import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Divider,
  Box
} from '@mui/material';

export interface AutoLayoutSettings {
  horizontalSpacing: number;
  verticalSpacing: number;
  sectionSpacing: number;
  sectionMargin: number;
  fontSize: number;
  labelWidth: number;
  labelFieldGap: number;
  fieldSpacingInRow: number;
  labelPadding: number;
}

export const DEFAULT_AUTO_LAYOUT_SETTINGS: AutoLayoutSettings = {
  horizontalSpacing: 10, // Space between columns
  verticalSpacing: 1, // Space between rows
  sectionSpacing: 10, // Space between sections
  sectionMargin: 3, // Margin around sections
  fontSize: 12,
  labelWidth: 70,
  labelFieldGap: 10, // Space between label and field
  fieldSpacingInRow: 10, // Space between fields in same row
  labelPadding: 2 // Padding inside labels
};

interface AutoLayoutSettingsDialogProps {
  open: boolean;
  settings: AutoLayoutSettings;
  onClose: () => void;
  onSave: (settings: AutoLayoutSettings) => void;
}

export const AutoLayoutSettingsDialog: React.FC<AutoLayoutSettingsDialogProps> = ({
  open,
  settings,
  onClose,
  onSave
}) => {
  const [localSettings, setLocalSettings] = useState<AutoLayoutSettings>(settings);

  const handleChange = (field: keyof AutoLayoutSettings, value: number) => {
    setLocalSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onSave(localSettings);
    onClose();
  };

  const handleReset = () => {
    setLocalSettings(DEFAULT_AUTO_LAYOUT_SETTINGS);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Auto Layout Settings</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
          Spacing
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Horizontal Spacing"
            type="number"
            value={localSettings.horizontalSpacing}
            onChange={(e) => handleChange('horizontalSpacing', parseInt(e.target.value))}
            size="small"
            sx={{ flex: 1, minWidth: 150 }}
            helperText="Space between columns (px)"
          />
          <TextField
            label="Vertical Spacing"
            type="number"
            value={localSettings.verticalSpacing}
            onChange={(e) => handleChange('verticalSpacing', parseInt(e.target.value))}
            size="small"
            sx={{ flex: 1, minWidth: 150 }}
            helperText="Space between rows (px)"
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
          <TextField
            label="Section Spacing"
            type="number"
            value={localSettings.sectionSpacing}
            onChange={(e) => handleChange('sectionSpacing', parseInt(e.target.value))}
            size="small"
            sx={{ flex: 1, minWidth: 150 }}
            helperText="Space between sections (px)"
          />
          <TextField
            label="Section Margin"
            type="number"
            value={localSettings.sectionMargin}
            onChange={(e) => handleChange('sectionMargin', parseInt(e.target.value))}
            size="small"
            sx={{ flex: 1, minWidth: 150 }}
            helperText="Margin around sections (px)"
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          Field Configuration
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Font Size"
            type="number"
            value={localSettings.fontSize}
            onChange={(e) => handleChange('fontSize', parseInt(e.target.value))}
            size="small"
            sx={{ flex: 1, minWidth: 150 }}
            helperText="Default font size (pt)"
          />
          <TextField
            label="Label Width"
            type="number"
            value={localSettings.labelWidth}
            onChange={(e) => handleChange('labelWidth', parseInt(e.target.value))}
            size="small"
            sx={{ flex: 1, minWidth: 150 }}
            helperText="Default label width (px)"
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
          <TextField
            label="Label-Field Gap"
            type="number"
            value={localSettings.labelFieldGap}
            onChange={(e) => handleChange('labelFieldGap', parseInt(e.target.value))}
            size="small"
            sx={{ flex: 1, minWidth: 150 }}
            helperText="Space between label and field (px)"
          />
          <TextField
            label="Field Spacing (Row)"
            type="number"
            value={localSettings.fieldSpacingInRow}
            onChange={(e) => handleChange('fieldSpacingInRow', parseInt(e.target.value))}
            size="small"
            sx={{ flex: 1, minWidth: 150 }}
            helperText="Space between fields in same row (px)"
          />
        </Box>

        <TextField
          fullWidth
          label="Label Padding"
          type="number"
          value={localSettings.labelPadding}
          onChange={(e) => handleChange('labelPadding', parseInt(e.target.value))}
          size="small"
          margin="normal"
          helperText="Padding inside labels (px)"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleReset} color="warning">
          Reset to Defaults
        </Button>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
